import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import {
  saveAdminPlanFaq,
  deleteAdminPlanFaq,
  type PlanFaqPayload,
} from "@/services/adminService";
import type { AdminPlanFaq, AdminPlansBundle } from "@/types/admin";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import {
  adminCard,
  adminTh,
  adminTd,
  adminBtnPrimary,
  adminBtnGhost,
  adminBtnDanger,
  adminIconBtn,
  adminInput,
  adminLabel,
} from "@/pages/portal/admin/config/ui";

interface FaqForm {
  question: string;
  answer: string;
  sort: number;
  isActive: boolean;
}

const EMPTY_FORM: FaqForm = { question: "", answer: "", sort: 1, isActive: true };

interface FaqTabProps {
  token: string | null;
  data: AdminPlansBundle;
  setData: Dispatch<SetStateAction<AdminPlansBundle>>;
  loading: boolean;
}

/** Admin tab — the "Questions about plans and billing" FAQ accordion. */
export default function FaqTab({ token, data, setData, loading }: FaqTabProps) {
  const [search, setSearch] = useState("");
  const [form, setForm] = useState<FaqForm | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AdminPlanFaq | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = [...data.faqs].sort((a, b) => a.sort - b.sort);
    if (!q) return rows;
    return rows.filter((f) => `${f.question} ${f.answer}`.toLowerCase().includes(q));
  }, [data.faqs, search]);

  function openCreate() {
    const nextSort = data.faqs.reduce((max, f) => Math.max(max, f.sort), 0) + 1;
    setForm({ ...EMPTY_FORM, sort: nextSort });
    setEditId(null);
  }

  function openEdit(row: AdminPlanFaq) {
    setForm({ question: row.question, answer: row.answer, sort: row.sort, isActive: row.isActive });
    setEditId(row.id);
  }

  function setField<K extends keyof FaqForm>(key: K, value: FaqForm[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function submit() {
    if (!form?.question.trim() || !form.answer.trim()) return;
    const payload: PlanFaqPayload = {
      question: form.question.trim(),
      answer: form.answer.trim(),
      sort: form.sort,
      isActive: form.isActive,
    };

    setBusy(true);
    try {
      await saveAdminPlanFaq(token, payload, editId ?? undefined);
      if (editId) {
        setData((prev) => ({
          ...prev,
          faqs: prev.faqs.map((f) => (f.id === editId ? { ...f, ...payload } as AdminPlanFaq : f)),
        }));
      } else {
        setData((prev) => ({
          ...prev,
          faqs: [...prev.faqs, { id: Date.now(), ...(payload as Omit<AdminPlanFaq, "id">) } as AdminPlanFaq],
        }));
      }
      setForm(null);
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(row: AdminPlanFaq) {
    const next = !row.isActive;
    setData((prev) => ({
      ...prev,
      faqs: prev.faqs.map((f) => (f.id === row.id ? { ...f, isActive: next } : f)),
    }));
    await saveAdminPlanFaq(token, { isActive: next }, row.id);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteAdminPlanFaq(token, deleteTarget.id);
      setData((prev) => ({ ...prev, faqs: prev.faqs.filter((f) => f.id !== deleteTarget.id) }));
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <AdminSearchInput value={search} onChange={setSearch} placeholder="Search plan FAQs…" />
        <button type="button" onClick={openCreate} className={adminBtnPrimary}>
          <i className="ri-add-line text-sm" /> New FAQ
        </button>
        <span className="ml-auto text-xs text-foreground-500">
          {loading ? "Loading…" : `${data.faqs.length} FAQs`}
        </span>
      </div>

      <div className={adminCard}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-question-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No FAQs yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse">
              <thead className="border-b border-background-200">
                <tr>
                  <th className={adminTh}>Order</th>
                  <th className={adminTh}>Question</th>
                  <th className={adminTh}>Visible</th>
                  <th className={`${adminTh} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filtered.map((row) => (
                  <tr key={row.id} className="hover:bg-background-100/40">
                    <td className={`${adminTd} tabular-nums`}>{row.sort}</td>
                    <td className={adminTd}>
                      <p className="font-semibold text-foreground-900">{row.question}</p>
                      <p className="mt-0.5 line-clamp-2 max-w-xl text-xs text-foreground-500">{row.answer}</p>
                    </td>
                    <td className={adminTd}>
                      <button
                        type="button"
                        onClick={() => toggleActive(row)}
                        className={`inline-flex cursor-pointer items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold transition-colors ${
                          row.isActive
                            ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                            : "bg-background-200 text-foreground-600 hover:bg-background-300"
                        }`}
                      >
                        {row.isActive ? "Visible" : "Hidden"}
                      </button>
                    </td>
                    <td className={`${adminTd} text-right`}>
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => openEdit(row)} className={adminIconBtn} title="Edit FAQ">
                          <i className="ri-pencil-line text-base" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(row)}
                          className={adminIconBtn}
                          title="Delete FAQ"
                        >
                          <i className="ri-delete-bin-line text-base text-rose-600" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {form && (
        <AdminModal
          title={editId ? "Edit FAQ" : "New FAQ"}
          subtitle="Shown in the plans FAQ accordion"
          wide
          onClose={() => setForm(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setForm(null)} className={adminBtnGhost}>
                Cancel
              </button>
              <button
                type="button"
                disabled={!form.question.trim() || !form.answer.trim() || busy}
                onClick={submit}
                className={adminBtnPrimary}
              >
                {editId ? "Save changes" : "Create FAQ"}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className={adminLabel} htmlFor="pf-question">Question</label>
              <input
                id="pf-question"
                type="text"
                value={form.question}
                onChange={(e) => setField("question", e.target.value)}
                placeholder="e.g. Can I upgrade my plan later?"
                className={adminInput}
              />
            </div>
            <div>
              <label className={adminLabel} htmlFor="pf-answer">Answer</label>
              <textarea
                id="pf-answer"
                value={form.answer}
                onChange={(e) => setField("answer", e.target.value)}
                rows={5}
                maxLength={500}
                placeholder="Write a clear, helpful answer…"
                className={`${adminInput} h-auto resize-y py-2.5 leading-relaxed`}
              />
              <p className="mt-1 text-right text-[11px] text-foreground-400">{form.answer.length}/500</p>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className={adminLabel} htmlFor="pf-sort">Sort order</label>
                <input
                  id="pf-sort"
                  type="number"
                  min={0}
                  value={form.sort}
                  onChange={(e) => setField("sort", Number(e.target.value) || 0)}
                  className={adminInput}
                />
              </div>
              <label className="mt-6 flex cursor-pointer items-center gap-2.5 text-sm text-foreground-700">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setField("isActive", e.target.checked)}
                  className="h-4 w-4 cursor-pointer accent-primary-500"
                />
                Visible on the pricing page
              </label>
            </div>
          </div>
        </AdminModal>
      )}

      {deleteTarget && (
        <AdminModal
          title="Delete FAQ"
          subtitle={deleteTarget.question}
          onClose={() => setDeleteTarget(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className={adminBtnGhost}>
                Cancel
              </button>
              <button type="button" disabled={busy} onClick={confirmDelete} className={adminBtnDanger}>
                <i className="ri-delete-bin-line text-sm" /> Delete FAQ
              </button>
            </div>
          }
        >
          <p className="text-sm text-foreground-700">
            Delete this FAQ? <strong>{deleteTarget.question}</strong>
          </p>
        </AdminModal>
      )}
    </div>
  );
}